import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { languages, type LanguageCode } from "@/i18n/config";
import { switchLangInPath } from "@/hooks/useLocalizedPath";
import { cn } from "@/lib/utils";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LanguageSwitcherProps {
  variant?: "default" | "compact" | "header";
  className?: string;
}

// Professional SVG flag components
const FlagGB = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 640 480" className={className}>
    <path fill="#012169" d="M0 0h640v480H0z"/>
    <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
    <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
    <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
  </svg>
);

const FlagES = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 640 480" className={className}>
    <path fill="#AA151B" d="M0 0h640v480H0z"/>
    <path fill="#F1BF00" d="M0 120h640v240H0z"/>
  </svg>
);

const flagComponents: Record<string, React.FC<{ className?: string }>> = {
  en: FlagGB,
  es: FlagES,
};

export function LanguageSwitcher({ variant = "default", className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  
  // Handle language variants like 'en-US' -> 'en'
  const normalizedLang = i18n.language?.split('-')[0] || 'en';
  const currentLanguage = languages.find(lang => lang.code === normalizedLang) || languages[0];
  const CurrentFlag = flagComponents[currentLanguage.code] || FlagGB;

  const handleLanguageChange = (code: LanguageCode) => {
    // Navigate to the same page but with the new language prefix
    const newPath = switchLangInPath(location.pathname, code);

    try {
      sessionStorage.setItem("preserve-scroll-on-language-switch", "true");
      sessionStorage.setItem("preserve-scroll-on-language-switch-y", String(window.scrollY));
    } catch (error) {
      // Ignore storage issues and fall back to default route scroll behavior
    }

    i18n.changeLanguage(code);
    navigate(newPath + location.search + location.hash);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label="Select language"
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
          "text-sm text-white/70 hover:text-white hover:bg-white/[0.06]",
          "border border-transparent hover:border-white/[0.1]",
          "focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50",
          variant === "compact" && "px-2 py-1.5",
          variant === "header" && "min-h-[44px] gap-1.5 rounded-full border border-transparent bg-transparent px-2 py-2 text-white/72 shadow-none backdrop-blur-0 hover:bg-transparent hover:text-white focus-visible:ring-primary/40",
          className
        )}
      >
        <Globe className={cn("w-4 h-4", (variant === "compact" || variant === "header") && "w-3.5 h-3.5")} />
        {variant !== "header" && (
          <span className={cn(
            "overflow-hidden rounded-sm shadow-sm",
            variant === "compact" ? "w-4 h-3" : "w-5 h-3.5"
          )}>
            <CurrentFlag className="w-full h-full object-cover" />
          </span>
        )}
        {variant === "default" && (
          <span className="hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
        )}
        {variant === "header" && (
          <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-current">
            {currentLanguage.code}
          </span>
        )}
      </DropdownMenuTrigger>
      
      <DropdownMenuContent
        align="end"
        className="min-w-[160px] bg-[#0a0a0f] border border-white/10 rounded-xl shadow-2xl shadow-black/50 p-1.5 z-[100]"
      >
        {languages.map((lang) => {
          const FlagComponent = flagComponents[lang.code] || FlagGB;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer",
                "text-sm text-white/80 hover:text-white hover:bg-white/[0.06]",
                "transition-all duration-150",
                currentLanguage.code === lang.code && "bg-primary/10 text-white"
              )}
            >
              <span className="w-5 h-3.5 overflow-hidden rounded-sm shadow-sm">
                <FlagComponent className="w-full h-full object-cover" />
              </span>
              <span className="flex-1">{lang.name}</span>
              {currentLanguage.code === lang.code && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
